import React from 'react';
import {Divider, Stack, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {map} from 'rxjs';
import {useParams} from 'react-router-dom';

import {ManageSuppliersService} from '@store/suppliers/services';
import {suppliersUnitsService} from '@store/suppliers/units';
import {serviceKindsService, ServiceKindTypeEnum, UnitOfMeasure} from '@store/settings/serviceKinds';
import {containersService} from '@store/settings/containers';
import {wasteFamiliesService} from '@store/settings/wasteFamilies';
import {suppliersService} from '@store/suppliers';

import {Sizes} from '@constants/sizes.constants';

import AsyncSelectWithSearchComponent from '@components/inputs/asyncSelect/AsyncSelectWithSearch.component';
import TextFieldComponent from '@components/inputs/textField/TextField.component';

interface DetailsFormProps {
  fromSupplier?: boolean;
  isCreation?: boolean;
  service: ManageSuppliersService;
  updateService: (data: ManageSuppliersService) => void;
  errors: { [key: string]: any };
}

const DetailsForm = (props: DetailsFormProps) => {
  const {fromSupplier, isCreation, service, errors, updateService} = props;

  const {t} = useTranslation();
  const {supplierId} = useParams();

  return (
    <>
      <Stack py="20px" spacing={2}>
        <Typography fontSize={16} fontWeight="700">
          {t('suppliers.details.catalog.manage.details')}
        </Typography>
        <Stack spacing={2}>
          <TextFieldComponent
            labelWidth={Sizes.drawerInputLabelWidth}
            label={t('suppliers.details.catalog.manage.name')}
            placeholder={t('global.writeSomething')}
            handleChange={(supplierName) => updateService({supplierName})}
            value={service.supplierName || ''}
            error={errors.supplierName}
            required/>
          {!fromSupplier && (
            <>
              {isCreation ? (
                <AsyncSelectWithSearchComponent
                  labelWidth={Sizes.drawerInputLabelWidth}
                  label={t('suppliers.details.catalog.manage.supplier')}
                  placeholder={t('global.selectOption')}
                  handleChange={(supplier) => updateService({supplier})}
                  value={service.supplier}
                  getOptions={(search) => suppliersService.searchSuppliers(search).pipe(
                    map((options) => options.data.map((o) => ({label: o.name || '-', value: o.id}))),
                  )}
                  error={errors.supplier}
                  required/>
              ) : (
                <TextFieldComponent
                  labelWidth={Sizes.drawerInputLabelWidth}
                  label={t('suppliers.details.catalog.manage.supplier')}
                  value={service.supplier?.label || ''}
                  readOnly
                  required/>
              )}
            </>
          )}
          {isCreation ? (
            <AsyncSelectWithSearchComponent
              labelWidth={Sizes.drawerInputLabelWidth}
              label={t('suppliers.details.catalog.manage.site')}
              placeholder={t('global.selectOption')}
              handleChange={(supplierUnit) =>
                updateService({
                  supplierUnit: supplierUnit ? {
                    id: supplierUnit.value,
                    name: supplierUnit.label,
                  } : undefined,
                })
              }
              value={service.supplierUnit ? {
                label: service.supplierUnit.name,
                value: service.supplierUnit.id
              } : undefined}
              getOptions={(search) => suppliersUnitsService.searchSuppliersUnits(search, [service.supplier?.value || supplierId]).pipe(
                map((options) => options.data.map((o) => ({label: o.name || '-', value: o.id}))),
              )}
              error={errors.supplierUnit}
              required/>
          ) : (
            <TextFieldComponent
              labelWidth={Sizes.drawerInputLabelWidth}
              label={t('suppliers.details.catalog.manage.site')}
              value={service.supplierUnit?.name || ''}
              readOnly
              required/>
          )}
          {isCreation ? (
            <AsyncSelectWithSearchComponent
              labelWidth={Sizes.drawerInputLabelWidth}
              label={t('suppliers.details.catalog.manage.serviceKind')}
              placeholder={t('global.selectOption')}
              handleChange={(serviceKind) => updateService({
                serviceKind: serviceKind ? {
                  id: serviceKind.value,
                  kind: serviceKind.label,
                  execUnitName1: serviceKind.data,
                  execUnitName2: UnitOfMeasure.UNIT,
                  kindTranslateKey: '',
                  type: ServiceKindTypeEnum.OTHER
                } : undefined,
              })}
              value={service.serviceKind ? {
                label: service.serviceKind.kind,
                value: service.serviceKind.id
              } : undefined}
              getOptions={(search) => serviceKindsService.searchServiceKinds(search).pipe(
                map((options) => options.data.map((o) => ({
                  label: o.kind,
                  value: o.id,
                  data: o.execUnitName1,
                })))
              )}
              error={errors.serviceKind}
              required/>
          ) : (
            <TextFieldComponent
              labelWidth={Sizes.drawerInputLabelWidth}
              label={t('suppliers.details.catalog.manage.serviceKind')}
              value={service.serviceKind?.kind || ''}
              readOnly
              required/>
          )}
          {isCreation ? (
            <AsyncSelectWithSearchComponent
              labelWidth={Sizes.drawerInputLabelWidth}
              label={t('suppliers.details.catalog.manage.container')}
              placeholder={t('global.selectOption')}
              handleChange={(container) => updateService({container})}
              value={service.container}
              getOptions={(search) => containersService.searchContainers(search).pipe(
                map((options) => options.data.map((o) => ({label: o.name, value: o.id})))
              )}/>
          ) : (
            <TextFieldComponent
              labelWidth={Sizes.drawerInputLabelWidth}
              label={t('suppliers.details.catalog.manage.container')}
              value={service.container?.label || ''}
              readOnly
              required/>
          )}
          {isCreation ? (
            <AsyncSelectWithSearchComponent
              labelWidth={Sizes.drawerInputLabelWidth}
              label={t('suppliers.details.catalog.manage.wasteType')}
              placeholder={t('global.selectOption')}
              handleChange={(waste) => updateService({waste})}
              value={service.waste}
              getOptions={(search) => wasteFamiliesService.searchWaste(search).pipe(
                map((options) => options.data.map((o) => ({label: o.name, value: o.id})))
              )}/>
          ) : (
            <TextFieldComponent
              labelWidth={Sizes.drawerInputLabelWidth}
              label={t('suppliers.details.catalog.manage.wasteType')}
              value={service.waste?.label || ''}
              readOnly
              required/>
          )}
        </Stack>
      </Stack>
      <Divider variant="fullWidth" orientation="horizontal"/>
    </>
  );
};

export default DetailsForm;
